import React from 'react';
import { Tag, Tooltip } from 'antd';
import { CrownOutlined, ClockCircleOutlined, ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAppContext } from '../contexts/AppContext';

interface SubscriptionBadgeProps {
  compact?: boolean;
}

const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({ compact = false }) => {
  const { subscription, isTrial, trialDaysRemaining, isExpired, subscriptionStatus, currentPlan } = useAppContext();

  if (!subscription) return null;

  // Derive display config from status — no hardcoded plan comparisons
  const isActive = subscriptionStatus === 'Active';

  const statusColor = isExpired
    ? '#ff4d4f'
    : isTrial
    ? (trialDaysRemaining !== null && trialDaysRemaining <= 3 ? '#faad14' : '#7C3AED')
    : isActive
    ? '#00BAA7'
    : '#6B7280';

  const icon = isExpired
    ? <ExclamationCircleOutlined />
    : isTrial
    ? <ClockCircleOutlined />
    : isActive
    ? <CheckCircleOutlined />
    : <CrownOutlined />;

  // Label: plan + key status indicator
  const label = (() => {
    if (compact) return currentPlan;
    if (isTrial && trialDaysRemaining !== null) return `${currentPlan} · ${trialDaysRemaining}d trial`;
    if (isTrial) return `${currentPlan} · Trial`;
    if (isExpired) return `${currentPlan} · Expired`;
    return currentPlan;
  })();

  const tooltipText = isExpired
    ? `Subscription ${subscriptionStatus} — action required`
    : isTrial && trialDaysRemaining !== null
    ? `Trial: ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining`
    : isTrial
    ? `${currentPlan} — On Trial`
    : `${currentPlan} · ${subscriptionStatus}`;

  return (
    <Tooltip title={tooltipText} placement="bottomRight">
      <Tag
        icon={icon}
        style={{
          background: `${statusColor}14`,
          border: `1px solid ${statusColor}35`,
          color: statusColor,
          borderRadius: 20,
          padding: compact ? '1px 7px' : '2px 10px',
          fontSize: compact ? 10 : 11,
          fontWeight: 600,
          cursor: 'default',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          lineHeight: '18px',
        }}
      >
        {label}
      </Tag>
    </Tooltip>
  );
};

export default SubscriptionBadge;
